import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Loader2, Eye, EyeOff, Shield, Crown, Mail } from 'lucide-react';

const ProfileDialog = ({
  open,
  onOpenChange,
  user,
  profileForm,
  setProfileForm,
  passwordForm,
  setPasswordForm,
  profileLoading,
  showCurrentPassword,
  setShowCurrentPassword,
  showNewPassword,
  setShowNewPassword,
  twoFactorEnabled,
  show2FAConfirm,
  setShow2FAConfirm,
  twoFATogglePassword,
  setTwoFATogglePassword,
  isDemoProspect,
  onUpdateProfile,
  onChangePassword,
  onToggle2FA,
  onDeleteAccount,
  onUpgrade,
  weeklyBriefOptOut,
  onToggleWeeklyBrief,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>My Profile</DialogTitle>
        <DialogDescription>Manage your account settings</DialogDescription>
      </DialogHeader>
      <div className="space-y-6 py-2">
        {/* Personal Info */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#111614] border-b border-[#E6E8E6] pb-2">Personal Information</h3>
          <div>
            <Label>Name</Label>
            <div className="flex gap-2">
              <Input
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                placeholder="Your name"
                data-testid="profile-name-input"
              />
              <Button onClick={onUpdateProfile} disabled={profileLoading || !profileForm.name.trim()} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm shrink-0" data-testid="save-profile-btn">
                {profileLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input value={user.email} disabled className="bg-[#F6F5F2] text-[#8A948F]" />
            <p className="text-xs text-[#8A948F] mt-1">Email cannot be changed</p>
          </div>
        </div>

        {/* Subscription */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#111614] border-b border-[#E6E8E6] pb-2">Subscription</h3>
          <div className="flex items-center justify-between p-3 bg-[#F0F5F2] rounded">
            <div>
              <p className="text-sm font-medium text-[#111614] capitalize">
                {isDemoProspect ? 'Demo Account' : `${user.subscription_status || 'free'} Plan`}
              </p>
              <p className="text-xs text-[#8A948F]">
                {isDemoProspect 
                  ? `Expires ${user.demo_expires_at ? new Date(user.demo_expires_at).toLocaleDateString() : 'in 30 days'}`
                  : user.subscription_status === 'free' ? 'Upgrade for bank connections & more' 
                  : user.subscription_status === 'trial' ? 'Trial period active' : 'Full access enabled'}
              </p>
            </div>
            {(isDemoProspect || user.subscription_status === 'free' || user.subscription_status === 'trial') && (
              <Button size="sm" onClick={onUpgrade} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" data-testid="upgrade-from-profile-btn">
                <Crown className="w-3.5 h-3.5 mr-1" /> {isDemoProspect ? 'Convert' : 'Upgrade'}
              </Button>
            )}
          </div>
        </div>

        {/* Two-Factor Authentication */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#111614] border-b border-[#E6E8E6] pb-2">Two-Factor Authentication</h3>
          <div className="flex items-center justify-between p-3 bg-[#F0F5F2] rounded">
            <div>
              <p className="text-sm font-medium text-[#111614]">{twoFactorEnabled ? 'Enabled' : 'Disabled'}</p>
              <p className="text-xs text-[#8A948F]">
                {twoFactorEnabled ? 'A code is sent to your email on each login' : 'Add an extra layer of security to your account'}
              </p>
            </div>
            <Button
              size="sm"
              variant={twoFactorEnabled ? "outline" : "default"}
              onClick={() => { setShow2FAConfirm(true); setTwoFATogglePassword(''); }}
              className={twoFactorEnabled ? "border-[#E6E8E6]" : "bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"}
              data-testid="toggle-2fa-btn"
            >
              <Shield className="w-3.5 h-3.5 mr-1" /> {twoFactorEnabled ? 'Disable' : 'Enable'}
            </Button>
          </div>
          {show2FAConfirm && (
            <div className="p-3 bg-[#FAFAF8] rounded border border-[#E6E8E6] space-y-3">
              <p className="text-xs text-[#5C6661]">Enter your password to {twoFactorEnabled ? 'disable' : 'enable'} 2FA:</p>
              <Input
                type="password"
                value={twoFATogglePassword}
                onChange={(e) => setTwoFATogglePassword(e.target.value)}
                placeholder="Your password"
                data-testid="2fa-confirm-password"
                onKeyDown={(e) => { if (e.key === 'Enter') onToggle2FA(); }}
              />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShow2FAConfirm(false)}>Cancel</Button>
                <Button size="sm" onClick={onToggle2FA} disabled={profileLoading || !twoFATogglePassword} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" data-testid="confirm-2fa-toggle-btn">
                  {profileLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Change Password */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#111614] border-b border-[#E6E8E6] pb-2">Change Password</h3>
          <div>
            <Label>Current Password</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Enter current password"
                data-testid="current-password-input"
                className="pr-10"
              />
              <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6661] hover:text-[#111614]" tabIndex={-1}>
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>New Password</Label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Enter new password"
                data-testid="new-password-input"
                className="pr-10"
              />
              <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6661] hover:text-[#111614]" tabIndex={-1}>
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              placeholder="Confirm new password"
              data-testid="confirm-password-input"
            />
          </div>
          <Button
            onClick={onChangePassword}
            disabled={profileLoading || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
            className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm w-full"
            data-testid="change-password-btn"
          >
            {profileLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change Password'}
          </Button>
        </div>

        {/* Notification Preferences */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#111614] border-b border-[#E6E8E6] pb-2 flex items-center gap-1.5">
            <Mail className="w-4 h-4" /> Email Preferences
          </h3>
          <div className="flex items-center justify-between p-3 bg-[#F4F1EA] rounded border border-[#E6E8E6]">
            <div className="flex-1 mr-3">
              <p className="text-sm font-medium text-[#111614]">Weekly AI Finance Brief</p>
              <p className="text-xs text-[#5C6661] mt-0.5">Receive a 3-point AI summary of your finances every 7 days. Paid plans only.</p>
            </div>
            <Switch
              checked={!weeklyBriefOptOut}
              onCheckedChange={(v) => onToggleWeeklyBrief(!v)}
              data-testid="weekly-brief-toggle"
            />
          </div>
        </div>

        {/* Danger Zone */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-red-600 border-b border-red-200 pb-2">Danger Zone</h3>
          <div className="flex items-center justify-between p-3 bg-red-50 rounded border border-red-100">
            <div>
              <p className="text-sm font-medium text-red-800">Delete Account</p>
              <p className="text-xs text-red-600">Permanently delete your account and all data</p>
            </div>
            <Button variant="destructive" size="sm" onClick={onDeleteAccount} data-testid="delete-account-btn">
              Delete
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default ProfileDialog;
